import { Layer } from "./Layer.js";

export { TileLayer }

/**
 * WMS Layer Implementation
 * 
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @memberof vef.map.layer
 */
class TileLayer extends Layer {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        // calling parent constructor
        super(config, cache, id);

        // init default values and define getters and setters
        this.addDefaults_({
            url: "",
            // overwrite existing defaults
            opacitySettings: true,
            availableCrs: ["EPSG:3857"],
            filterDisabled: true
        });

        this.setAll_("type", "tile");

        // internal properties
        this.zIndex_ = 0;
    }

    /**
     * create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_(type) {
        let layer = null;
        switch (type) {
            case "leaflet":
                layer = L.tileLayer(this.url, {
                    attribution: this.attribution,
                    maxZoom: 18
                });

                break;
        }

        if (layer) {
            layer.on('loading', () => this.fire("loading"));
            layer.on('load', () => this.fire("load"));
            this.mapLayers_[type] = layer;
        }

        return layer;
    }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_(type) {
        this.activeMap_ = type;

        // call setters to apply options to the active mapLayer
        if (Number.isFinite(this.opacity)) this.setOpacity(this.opacity);
        if (Number.isFinite(this.zIndex_)) this.setZIndex(this.zIndex_);
        this.setProjection(this.crs_);

        // reload was intentionally prevented in the setters to call it in the end
        this.reload();
    }

    /**
     * Switches the projection for the visualization
     * of the leaflet layer. CRS hast to be included in availableCRS
     * 
     * @param {string} crs target crs code
     * @returns {boolean} returns true if successful
     */
    setProjection(crs) {
        const code = crs.replace(":", "");
        if (this.availableCrs.includes(crs) && (code in L.CRS)) {
            this.crs_ = crs;
            if (this.activeMap_ in this.mapLayers_) {
                switch (this.activeMap_) {
                    case "leaflet":
                        this.mapLayers_.leaflet.options.crs = L.CRS[code];
                }
            }
            return true;
        }
        return false;
    }

    /**
     * Reload the Layer on the map
     */
    reload() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.redraw();
                break;
        }
    }

    /**
     * Set the transparency between 0 and 1
     * @override
     * @param {number} opacity style name
     */
    setOpacity(opacity) {
        this.opacity = opacity;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.setOpacity(opacity);
                break;
        }
    }

    /**
     * Set the z-index of a layer
     * @param {number} index 
     */
    setZIndex(index) {
        this.zIndex_ = index;

        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.setZIndex(index);
                break;
        }
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        return {
            min: { y: -85, x: -180 },
            max: { y: 85, x: 180 },
            crs: "CRS:84"
        }
    }

}
